import React, { useEffect, useState, useCallback } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase, isConfigured } from './services/supabase';
import { getOrCreateDefaultTeam } from './services/teams';
import { checkAdminStatus } from '@rehub/admin';
import { getPendingPartnerApplicationsCount } from './services/partners';
import type { UserProfile, Team, SalesRoute } from './types';

import Login from './components/Login';
import Layout from './components/Layout';
import DashboardPage from './components/Dashboard/DashboardPage';
import LeadsPage from './components/Leads/LeadsPage';
import DealsPage from './components/Deals/DealsPage';
import TasksPage from './components/Tasks/TasksPage';
import ImportPage from './components/Import/ImportPage';
import SettingsPage from './components/Settings/SettingsPage';
import EarningsPage from './components/Earnings/EarningsPage';
import AdminCommissionsPage from './components/Admin/AdminCommissionsPage';
import AdminPartnersPage from './components/Admin/AdminPartnersPage';
import AdminPayoutsPage from './components/Admin/AdminPayoutsPage';
import InboxPage from './components/Inbox/InboxPage';
import CallsPage from './components/Calls/CallsPage';
import Dialler from './components/Dialler/Dialler';
import QueuePage from './components/Queue/QueuePage';

function getRouteFromPath(): SalesRoute {
  const path = window.location.pathname;
  if (path === '/leads' || path === '/sales/leads') return 'leads';
  if (path === '/deals' || path === '/sales/deals') return 'deals';
  if (path === '/tasks' || path === '/sales/tasks') return 'tasks';
  if (path === '/import' || path === '/sales/import') return 'import';
  if (path === '/settings' || path === '/sales/settings') return 'settings';
  if (path === '/earnings' || path === '/sales/earnings') return 'earnings';
  if (path === '/inbox' || path === '/sales/inbox') return 'inbox';
  if (path === '/calls' || path === '/sales/calls') return 'calls';
  if (path === '/admin/commissions' || path === '/sales/admin/commissions') return 'admin-commissions';
  if (path === '/admin/partners' || path === '/sales/admin/partners') return 'admin-partners';
  if (path === '/admin/payouts' || path === '/sales/admin/payouts') return 'admin-payouts';
  if (path === '/queue' || path === '/sales/queue') return 'queue';
  return 'dashboard';
}

const App: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentRoute, setCurrentRoute] = useState<SalesRoute>(getRouteFromPath());
  const [pendingPartnerApps, setPendingPartnerApps] = useState(0);

  const fetchProfile = useCallback(async (userId: string) => {
    if (!isConfigured) return;
    const { data } = await supabase
      .from('profiles')
      .select('id, email, credits')
      .eq('id', userId)
      .single();
    if (data) {
      setUser(data as UserProfile);
    }
  }, []);

  const fetchTeam = useCallback(async (userId: string) => {
    if (!isConfigured) return;
    const teamData = await getOrCreateDefaultTeam(userId);
    if (teamData) {
      setTeam(teamData);
    }
  }, []);

  const fetchAdminStatus = useCallback(async (userId: string) => {
    if (!isConfigured) return;
    const admin = await checkAdminStatus(supabase, userId);
    setIsAdmin(admin);
    if (admin) {
      const count = await getPendingPartnerApplicationsCount();
      setPendingPartnerApps(count);
    }
  }, []);

  useEffect(() => {
    if (!isConfigured) {
      setLoading(false);
      return;
    }

    const hash = window.location.hash;
    if (hash.includes('sso_access_token') && hash.includes('sso_refresh_token')) {
      const params = new URLSearchParams(hash.slice(1));
      const accessToken = params.get('sso_access_token');
      const refreshToken = params.get('sso_refresh_token');
      if (accessToken && refreshToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }).then(({ data }) => {
          if (data.session) {
            setSession(data.session);
            fetchProfile(data.session.user.id);
            fetchTeam(data.session.user.id);
            fetchAdminStatus(data.session.user.id);
          }
          window.history.replaceState({}, document.title, window.location.pathname);
        });
      }
    }

    const currentPath = window.location.pathname;
    const authCode = new URLSearchParams(window.location.search).get('code');
    if (currentPath === '/auth/callback' && authCode) {
      supabase.auth.exchangeCodeForSession(authCode).then(({ data }) => {
        if (data.session) {
          setSession(data.session);
          fetchProfile(data.session.user.id);
          fetchTeam(data.session.user.id);
          fetchAdminStatus(data.session.user.id);
          window.history.replaceState({}, document.title, '/');
          setCurrentRoute('dashboard');
        }
        setLoading(false);
      });
      return;
    }

    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      if (existingSession) {
        fetchProfile(existingSession.user.id);
        fetchTeam(existingSession.user.id);
        fetchAdminStatus(existingSession.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession) {
        fetchProfile(newSession.user.id);
        fetchTeam(newSession.user.id);
        fetchAdminStatus(newSession.user.id);
      } else {
        setUser(null);
        setTeam(null);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, fetchTeam, fetchAdminStatus]);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentRoute(getRouteFromPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = useCallback((route: SalesRoute) => {
    const pathMap: Record<SalesRoute, string> = {
      dashboard: '/',
      leads: '/leads',
      deals: '/deals',
      tasks: '/tasks',
      import: '/import',
      settings: '/settings',
      earnings: '/earnings',
      inbox: '/inbox',
      calls: '/calls',
      'admin-commissions': '/admin/commissions',
      'admin-partners': '/admin/partners',
      'admin-payouts': '/admin/payouts',
      queue: '/queue',
    };
    window.history.pushState({}, '', pathMap[route]);
    setCurrentRoute(route);
    // Refresh pending count when navigating away from partners page (in case apps were reviewed)
    if (currentRoute === 'admin-partners' && route !== 'admin-partners' && isAdmin) {
      getPendingPartnerApplicationsCount().then(setPendingPartnerApps);
    }
  }, [currentRoute, isAdmin]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setTeam(null);
    setIsAdmin(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!session) {
    return <Login />;
  }

  const renderPage = () => {
    if (!team) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      );
    }

    switch (currentRoute) {
      case 'queue':
        return <QueuePage team={team} userId={session.user.id} isAdmin={isAdmin} />;
      case 'leads':
        return <LeadsPage team={team} userId={session.user.id} isAdmin={isAdmin} />;
      case 'deals':
        return <DealsPage team={team} userId={session.user.id} isAdmin={isAdmin} />;
      case 'tasks':
        return <TasksPage team={team} userId={session.user.id} isAdmin={isAdmin} />;
      case 'import':
        return <ImportPage team={team} />;
      case 'settings':
        return <SettingsPage team={team} userId={session.user.id} />;
      case 'earnings':
        return <EarningsPage team={team} userId={session.user.id} />;
      case 'inbox':
        return <InboxPage team={team} navigate={navigate} />;
      case 'calls':
        return <CallsPage team={team} navigate={navigate} />;
      case 'admin-commissions':
        return isAdmin ? (
          <AdminCommissionsPage team={team} userId={session.user.id} />
        ) : (
          <DashboardPage team={team} userId={session.user.id} navigate={navigate} isAdmin={isAdmin} />
        );
      case 'admin-partners':
        return isAdmin ? (
          <AdminPartnersPage team={team} userId={session.user.id} />
        ) : (
          <DashboardPage team={team} userId={session.user.id} navigate={navigate} isAdmin={isAdmin} />
        );
      case 'admin-payouts':
        return isAdmin ? (
          <AdminPayoutsPage team={team} userId={session.user.id} />
        ) : (
          <DashboardPage team={team} userId={session.user.id} navigate={navigate} isAdmin={isAdmin} />
        );
      default:
        return <DashboardPage team={team} userId={session.user.id} navigate={navigate} isAdmin={isAdmin} />;
    }
  };

  return (
    <Layout
      user={user}
      isAdmin={isAdmin}
      currentRoute={currentRoute}
      onNavigate={navigate}
      onLogout={handleLogout}
      pendingPartnerApplications={pendingPartnerApps}
    >
      {renderPage()}
      <Dialler />
    </Layout>
  );
};

export default App;
